// frontend/src/PatientDetail.js
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from './axiosConfig';
import PatientFiles from './PatientFiles';
import PatientReports from './PatientReports';
import SpecialCaseModal from './SpecialCaseModal';
import DateBox from './DateBox';
import PatientMessages from './PatientMessages';
import './PatientDetail.css';

const MFG_CODES = [
  'ED1','ED2','ED3','ED4','ED5','ED6','ED7',
  'PS1','PS2','PS3','PS4','PS5','PS6','PS7',
  'RD1','RD2','RD3','RD4','RD5','RD6','RD7'
];

const TOM_MAGIC_HEX = ['E8','54','4F','4D','0D','0A','20','0A','24','00','00','00','00','00','00','00'];
async function validateTomSignature(file) {
  if (!file || file.size < 16) return false;
  const slice = file.slice(0, 16);
  const buf = await slice.arrayBuffer();
  const bytes = new Uint8Array(buf);
  if (bytes.length < 16) return false;
  for (let i = 0; i < 16; i++) {
    const hex = bytes[i].toString(16).toUpperCase().padStart(2, '0');
    if (hex !== TOM_MAGIC_HEX[i]) return false;
  }
  return true;
}

const UNREAD_POLL_MSE = 60_000;

// ---- Date helpers ----
function isoToDisplay(iso) {
  if (!iso) return '';
  const s = iso.replace(/-/g, '/');
  const m = s.match(/^(\d{4})\/(\d{2})\/(\d{2})$/);
  if (!m) return '';
  return `${m[1]}/${m[2]}/${m[3]}`;
}
function isoToUS(iso) {
  if (!iso) return '';
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return '';
  return `${m[2]}/${m[3]}/${m[1]}`;
}

// ---- TOM filename helper ----
function lnameFname(patient) {
  const first = (patient?.first_name || '').replace(/\s+/g, '');
  const last  = (patient?.last_name  || '').replace(/\s+/g, '');
  return `${last}${first}`;
}
function makeTomFilename(patient, code) {
  const safeCode = (code || '').replace(/[^A-Za-z0-9#]/g, '');
  return `${lnameFname(patient)}${safeCode}.tom`;
}

const PatientDetail = () => {
  const { t, i18n } = useTranslation();
  const isZh = String(i18n.resolvedLanguage || i18n.language || '').toLowerCase().startsWith('zh');
  const locale = isZh ? 'zh-CN' : 'en-US';
  const formatPatientAge = (value) => {
    const match = String(value || '').match(/^(\d+)\s+years?,\s*(\d+)\s+months?$/i);
    if (!match) return value || '';
    return t('patient.ageYearsMonths', { years: Number(match[1]), months: Number(match[2]) });
  };
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [patient, setPatient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState(() => {
    const requested = searchParams.get('tab');
    return ['overview', 'files', 'reports', 'messages', 'order'].includes(requested) ? requested : (requested === 'recommendation' ? 'files' : 'overview');
  });

  // Submit Order – Band DSI Image Uploads
  const [orderValue, setOrderValue] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Upload status block
  const [uploadStatus, setUploadStatus] = useState({ state: 'idle', pct: 0, message: '' });

  // DSI date (ISO inside; displayed by DateBox)
  const [dsiDateISO, setDsiDateISO] = useState('');

  // Replacement & Special Case
  const [replacement, setReplacement] = useState(false);
  const [specialCase, setSpecialCase] = useState(false);
  const [showSpecialCaseModal, setShowSpecialCaseModal] = useState(false);
  const [specialCaseData, setSpecialCaseData] = useState(null);

  // Manufacturing upload state
  const [mfgFile, setMfgFile] = useState(null);
  const [mfgCode, setMfgCode] = useState('');
  const [mfgError, setMfgError] = useState('');

  // Previously uploaded .tom files (ORDER tab list)
  const [tomFiles, setTomFiles] = useState([]);
  const [tomLoading, setTomLoading] = useState(false);

  // Reports unread badge
  const [unreadReports, setUnreadReports] = useState(0);

  // Messages unread badge
  const [hasUnreadMessages, setHasUnreadMessages] = useState(false);

  const pollRef = useRef(null);
  const summaryReqRef = useRef(null);
  const orderListReqRef = useRef(null);

  useEffect(() => {
    const requested = searchParams.get('tab');
    if (requested === 'recommendation') {
      setActiveTab('files');
    } else if (requested && ['overview', 'files', 'reports', 'messages', 'order'].includes(requested)) {
      setActiveTab(requested);
    }
  }, [searchParams]);

  const changeTab = (tabKey) => {
    setActiveTab(tabKey);
    const next = new URLSearchParams(searchParams);
    if (tabKey === 'overview') next.delete('tab');
    else next.set('tab', tabKey);
    setSearchParams(next, { replace: true });
  };

  useEffect(() => {
    axios.get(`patients/${id}/`)
      .then((res) => {
        setPatient(res.data);
        if (res.data?.dsi_date) {
          const iso = res.data.dsi_date.replace(/\//g, '-');
          setDsiDateISO(iso);
        }
      })
      .catch((err) => {
        console.error(t('error.fetchPatient'), err);
        setError(t('error.loadPatient'));
      })
      .finally(() => setLoading(false));
  }, [id, t]);

  const fetchUnreadSummary = useCallback(async () => {
    try {
      if (summaryReqRef.current) summaryReqRef.current.abort?.();
      const controller = new AbortController();
      summaryReqRef.current = controller;

      const res = await axios.get(`patients/${id}/reports/summary/`, {
        params: { scan: 1, since_hours: 72 },
        signal: controller.signal
      });

      setUnreadReports(Number(res?.data?.unread || 0));
    } catch (e) {
      if (e?.name === 'CanceledError' || e?.code === 'ERR_CANCELED') return;
    }
  }, [id]);

  const loadTomFiles = useCallback(async () => {
    setTomLoading(true);
    try {
      if (orderListReqRef.current) orderListReqRef.current.abort?.();
      const controller = new AbortController();
      orderListReqRef.current = controller;

      const res = await axios.get(`patients/${id}/files/?include_azure_toms=1`, { signal: controller.signal });
      const list = Array.isArray(res.data?.files) ? res.data.files : (Array.isArray(res.data) ? res.data : []);
      const norm = list.map((f, idx) => {
        const leaf = (f.original_filename || f.filename || f.name || f.blob_name || `file-${idx}`).toString();
        const url  = f.blob_url || f.url || f.file_url || f.file || f.download_url || '';
        const ts   = f.created_at || f.uploaded_at || f.updated_at || '';
        return { name: leaf, url, created_at: ts };
      });
      setTomFiles(norm.filter(x => x.name.toLowerCase().endsWith('.tom')));
    } catch (e) {
      if (e?.name === 'CanceledError' || e?.code === 'ERR_CANCELED') return;
      setTomFiles([]);
    } finally {
      setTomLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchUnreadSummary();
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(fetchUnreadSummary, UNREAD_POLL_MSE);
    const onVis = () => { if (document.visibilityState === 'visible') fetchUnreadSummary(); };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      document.removeEventListener('visibilitychange', onVis);
      if (summaryReqRef.current) summaryReqRef.current.abort?.();
    };
  }, [fetchUnreadSummary]);

  useEffect(() => {
    if (activeTab === 'order') loadTomFiles();
    return () => {
      if (orderListReqRef.current) orderListReqRef.current.abort?.();
    };
  }, [activeTab, loadTomFiles]);

  const handleEdit = () => navigate(`/dashboard/patients/edit/${id}`);

  const onToggleSpecialCase = (checked) => {
    setSpecialCase(checked);
    if (checked) setShowSpecialCaseModal(true);
    else { setShowSpecialCaseModal(false); setSpecialCaseData(null); }
  };

  async function buildRenamedTomFile(origFile, code) {
    const newName = makeTomFilename(patient, code);
    const buf = await origFile.arrayBuffer();
    return new File([buf], newName, { type: origFile.type || 'application/octet-stream' });
  }

  const clearStatusSoon = () => {
    setTimeout(() => setUploadStatus({ state: 'idle', pct: 0, message: '' }), 2500);
  };

  const postManufacturingUpload = async (formData) => {
    setUploadStatus({ state: 'uploading', pct: 0, message: t('uploading', { defaultValue: 'Uploading…' }) });

    try {
      const res = await axios.post(`patients/${id}/submit-manufacturing-upload/`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 0,
        onUploadProgress: (evt) => {
          const total = evt?.total;
          const loaded = evt?.loaded;
          if (!total || !loaded) return;
          const pct = Math.round((loaded / total) * 100);
          const clamped = Math.max(0, Math.min(100, pct));

          if (clamped >= 100) {
            setUploadStatus({ state: 'waiting', pct: 100, message: t('waitingServer', { defaultValue: 'Upload sent — waiting for server…' }) });
          } else {
            setUploadStatus({ state: 'uploading', pct: clamped, message: t('uploading', { defaultValue: 'Uploading…' }) });
          }
        },
      });

      setUploadStatus({ state: 'success', pct: 100, message: t('uploadSuccess', { defaultValue: 'Upload successful.' }) });
      clearStatusSoon();
      return res;
    } catch (err) {
      const status = err?.response?.status;

      if (!status) {
        setUploadStatus({
          state: 'error',
          pct: 100,
          message: t('patient.order.uploadMayHaveCompleted'),
        });
        clearStatusSoon();
        throw new Error('AMBIGUOUS_UPLOAD');
      }

      const serverMsg = err?.response?.data?.detail || err?.response?.data?.error || err?.response?.data?.message;
      setUploadStatus({ state: 'error', pct: 100, message: (!isZh && serverMsg) ? serverMsg : t('patient.order.uploadFailed') });
      clearStatusSoon();
      throw err;
    }
  };

  const handleOrderSubmit = async (e) => {
    e.preventDefault();
    if (!patient || isSubmitting) return;

    setIsSubmitting(true);
    setMfgError('');
    setUploadStatus({ state: 'idle', pct: 0, message: '' });

    try {
      if (replacement) {
        if (!mfgFile) { setMfgError(t('patient.order.errReplacementFile')); setIsSubmitting(false); return; }
        if (!mfgFile.name.toLowerCase().endsWith('.tom')) { setMfgError(t('patient.order.errTomOnly')); setIsSubmitting(false); return; }
        if (!mfgCode) { setMfgError(t('patient.order.errMfgCode')); setIsSubmitting(false); return; }
        const ok = await validateTomSignature(mfgFile);
        if (!ok) { setMfgError(t('patient.order.errTomSignature')); setIsSubmitting(false); return; }

        const renamed = await buildRenamedTomFile(mfgFile, mfgCode);

        const formData = new FormData();
        formData.append('file', renamed);
        formData.append('processing_code', mfgCode);
        formData.append('replacement', 'true');
        if (dsiDateISO) formData.append('dsi_date', isoToUS(dsiDateISO));

        await postManufacturingUpload(formData);
        setPatient(prev => ({ ...prev, dsi_date: dsiDateISO || prev?.dsi_date || null }));

        setMfgFile(null); setMfgCode(''); setReplacement(false); setOrderValue('');
        await loadTomFiles();

      } else {
        if (!orderValue) { setMfgError(t('patient.order.errBillingCode')); setIsSubmitting(false); return; }
        const newDocNumber = orderValue;

        await axios.patch(`patients/${id}/`, {
          doc_number: newDocNumber,
          dsi_date: dsiDateISO || null,
        });

        if (specialCase) {
          if (!mfgFile) { setMfgError(t('patient.order.errSpecialCaseFile')); setIsSubmitting(false); return; }
          if (!mfgFile.name.toLowerCase().endsWith('.tom')) { setMfgError(t('patient.order.errTomOnly')); setIsSubmitting(false); return; }
          if (!mfgCode) { setMfgError(t('patient.order.errMfgCode')); setIsSubmitting(false); return; }
          const ok = await validateTomSignature(mfgFile);
          if (!ok) { setMfgError(t('patient.order.errTomSignature')); setIsSubmitting(false); return; }
          if (!specialCaseData) { setMfgError(t('patient.order.errSpecialCaseForm')); setIsSubmitting(false); return; }

          const renamed = await buildRenamedTomFile(mfgFile, orderValue);

          const formData = new FormData();
          formData.append('file', renamed);
          formData.append('processing_code', mfgCode);
          formData.append('special_case', 'true');

          const scDsiISO = specialCaseData.dsi_date
            ? (specialCaseData.dsi_date.replace(/\//g, '-'))
            : dsiDateISO;
          if (scDsiISO) formData.append('dsi_date', isoToUS(scDsiISO));

          formData.append('surgery_types', (specialCaseData.surgery_types || []).join(','));
          formData.append('special_issues', (specialCaseData.special_issues || []).join(','));
          formData.append('hemangioma_location', specialCaseData.hemangioma_location || '');
          formData.append('surgery_date', specialCaseData.surgery_date || '');
          formData.append('suture', specialCaseData.suture || '');
          formData.append('surgeon', specialCaseData.surgeon || '');
          formData.append('notes', specialCaseData.notes || '');
          if (specialCaseData.age) formData.append('age', String(specialCaseData.age));

          await postManufacturingUpload(formData);
          if (scDsiISO) setDsiDateISO(scDsiISO);
          setPatient(prev => ({ ...prev, doc_number: newDocNumber, dsi_date: scDsiISO || dsiDateISO || null }));

          setSpecialCase(false);
          setSpecialCaseData(null);
          await loadTomFiles();

        } else {
          if (mfgFile) {
            if (!mfgFile.name.toLowerCase().endsWith('.tom')) { setMfgError(t('patient.order.errTomOnly')); setIsSubmitting(false); return; }
            if (!mfgCode) { setMfgError(t('patient.order.errMfgCode')); setIsSubmitting(false); return; }
            const ok = await validateTomSignature(mfgFile);
            if (!ok) { setMfgError(t('patient.order.errTomSignature')); setIsSubmitting(false); return; }

            const renamed = await buildRenamedTomFile(mfgFile, orderValue);

            const formData = new FormData();
            formData.append('file', renamed);
            formData.append('processing_code', mfgCode);
            if (dsiDateISO) formData.append('dsi_date', isoToUS(dsiDateISO));

            await postManufacturingUpload(formData);
            await loadTomFiles();
          } else {
            await axios.post(`patients/${id}/order-submissions/`, {
              submission_type: 'standard',
              doc_number: newDocNumber,
              dsi_date: dsiDateISO || null,
              processing_code: '',
              has_file: false,
            });
            setUploadStatus({ state: 'success', pct: 100, message: t('patient.order.saved') });
            clearStatusSoon();
          }
        }

        if (!specialCase) {
          setPatient(prev => ({ ...prev, doc_number: newDocNumber, dsi_date: dsiDateISO || null }));
        }
        setMfgFile(null); setMfgCode(''); setOrderValue('');
      }
    } catch (err) {
      if (err?.message === 'AMBIGUOUS_UPLOAD') {
        try { await loadTomFiles(); } catch (_) {}
        setIsSubmitting(false);
        return;
      }
      setIsSubmitting(false);
      return;
    }

    setIsSubmitting(false);
  };

  if (loading) return <div className="pd-container">{t('loading.patientDetails')}</div>;
  if (error)   return <div className="pd-container pd-error">{error}</div>;
  if (!patient) return <div className="pd-container">{t('error.noPatient')}</div>;

  const tabs = [
    { key: 'overview', label: t('tab.overview') },
    { key: 'files',    label: t('patient.order.tabFiles') },
    {
      key: 'reports',
      label: (
        <span className="pd-tab-with-badge">
          {t('patient.order.tabReports')}
          {unreadReports > 0 && (
            <span className="pd-badge-unread" title={t('patient.order.reportsAwaitingReview')}>
              {unreadReports}
            </span>
          )}
        </span>
      )
    },
    {
      key: 'messages',
      label: (
        <span className="pd-tab-with-badge">
          {t('patient.order.tabMessages')}
          {hasUnreadMessages && (
            <span className="pd-badge-unread" title={t('patient.order.newReplies')}>
              !
            </span>
          )}
        </span>
      )
    },
    { key: 'order', label: t('patient.order.tabOrder') }
  ];

  const {
    first_name,
    last_name,
    date_of_birth,
    distributors_mrn,
    service_center,
    service_center_detail,
    gender,
    created_at,
    updated_at,
    doc_number,
    age
  } = patient;

  const serviceCenterName =
    (service_center_detail && service_center_detail.name) ||
    (service_center && service_center.name) ||
    t('nA');

  const formatPortalDateTime = (value) => {
    if (!value) return t('nA');
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? value : d.toLocaleString(locale);
  };

  const showStatusBlock = uploadStatus.state !== 'idle';

  return (
    <div className="pd-container">
      <div className="pd-header pd-header--modern">
        <div className="pd-patient-heading">
          <button type="button" className="pd-back-link" onClick={() => navigate('/dashboard/patients')}>
            ← {t('patients', { defaultValue: 'Patients' })}
          </button>
          <div className="pd-patient-title-row">
            <div className="pd-avatar" aria-hidden="true">
              {(first_name?.[0] || '').toUpperCase()}{(last_name?.[0] || '').toUpperCase()}
            </div>
            <div>
              <div className="pd-eyebrow">{t('patientDetails')}</div>
              <h2>{first_name} {last_name}</h2>
              <div className="pd-patient-meta">
                <span>{serviceCenterName}</span>
                <span>{t('distributorsMRN')}: {distributors_mrn || t('nA')}</span>
                <span>{t('dateOfBirth')}: {date_of_birth || t('nA')}</span>
                {patient.dsi_recommendation_enabled && patient.latest_recommendation_status && (
                  <span className={`pd-rec-status ${patient.latest_recommendation_status === 'treatment_recommended' ? 'is-recommended' : 'is-not-recommended'}`}>
                    {patient.latest_recommendation_status === 'treatment_recommended' ? t('recommendation.recommended') : t('recommendation.notRecommended')}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
        <button type="button" onClick={handleEdit} className="pd-header-edit">
          {t('editPatient')}
        </button>
      </div>

      <div className="pd-tabs" role="tablist">
        {tabs.map(tab => (
          <button
            key={tab.key}
            role="tab"
            aria-selected={activeTab === tab.key}
            className={
              `pd-tab ${activeTab === tab.key ? 'active' : ''} ` +
              `${tab.key === 'files' ? 'pd-tab--green' : ''} ` +
              `${tab.key === 'order' ? 'pd-tab--red' : ''}`
            }
            onClick={() => changeTab(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="pd-content">
        {activeTab === 'overview' && (
          <div className="pd-overview">
            <div className="pd-grid">
              <div><strong>{t('firstName')}:</strong> {first_name}</div>
              <div><strong>{t('lastName')}:</strong> {last_name}</div>
              <div><strong>{t('dateOfBirth')}:</strong> {date_of_birth || t('nA')}</div>
              <div><strong>{t('age')}:</strong> {age || t('nA')}</div>
              <div><strong>{t('docNumber')}:</strong> {doc_number || t('nA')}</div>
              <div><strong>{t('distributorsMRN')}:</strong> {distributors_mrn || t('nA')}</div>
              <div><strong>{t('serviceCenter')}:</strong> {serviceCenterName}</div>
              <div><strong>{t('gender')}:</strong> {t(`gender.${gender}`, { defaultValue: gender })}</div>
              <div><strong>{t('createdAt')}:</strong> {formatPortalDateTime(created_at)}</div>
              <div><strong>{t('updatedAt')}:</strong> {formatPortalDateTime(updated_at)}</div>
            </div>
          </div>
        )}

        {activeTab === 'files' && (
          <PatientFiles
            patientId={id}
            patient={patient}
            hideSendForDSICheckbox={true}
            showDSIDropdownDefault={true}
            excludeExtensions={['.docx', '.pdf']}
            onRecommendationChange={(recFields) => setPatient((prev) => ({ ...prev, ...recFields }))}
            key={`files-${id}`}
          />
        )}

        {activeTab === 'reports' && (
          <PatientReports
            patientId={id}
            active={activeTab === 'reports'}
            onUnreadChange={(unread) => setUnreadReports(Number(unread || 0))}
            onCountsChange={(counts) => setUnreadReports(Number(counts?.unread || 0))}
            key={`reports-${id}`}
          />
        )}

        {activeTab === 'messages' && (
          <PatientMessages
            patientId={id}
            active={activeTab === 'messages'}
            onUnreadChange={(hasNew) => setHasUnreadMessages(!!hasNew)}
            enableTranslation={true}
          />
        )}

        {activeTab === 'order' && (
          <div className="pd-order">
            <h3>{t('patient.order.title')}</h3>

            <form onSubmit={handleOrderSubmit} className="pd-form">
              <fieldset disabled={isSubmitting} style={{ border: 'none', padding: 0, margin: 0 }}>
                <div className="pd-field" style={{ gridColumn: '1 / -1' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input
                      type="checkbox"
                      checked={replacement}
                      onChange={(e) => setReplacement(e.target.checked)}
                    />
                    <span style={{ whiteSpace: 'nowrap' }}>
                      <strong>{t('replacement')}:</strong> {t('patient.order.replacementHelp')}
                    </span>
                  </label>
                </div>

                <div className="pd-field" style={{ gridColumn: '1 / -1' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input
                      type="checkbox"
                      checked={specialCase}
                      onChange={(e) => onToggleSpecialCase(e.target.checked)}
                    />
                    {t('specialCase', { defaultValue: 'Special Case' })}
                  </label>
                </div>

                <div className="pd-field">
                  <label>{t('patient.order.billingCode')}</label>
                  <select
                    value={orderValue}
                    onChange={(e) => setOrderValue(e.target.value)}
                    required={!replacement}
                    disabled={replacement}
                  >
                    <option value="">{t('patient.order.selectBillingCode')}</option>
                    <option value="ED1">ED1</option><option value="ED2">ED2</option><option value="ED3">ED3</option>
                    <option value="ED4">ED4</option><option value="ED5">ED5</option><option value="ED6">ED6</option>
                    <option value="ED7">ED7</option>
                    <option value="1RK">1RK</option><option value="#2/1RK">#2/1RK</option>
                    <option value="2RK">2RK</option><option value="#2/2RK">#2/2RK</option><option value="9RK">9RK</option>
                    <option value="PSD1">PSD1</option><option value="PSD2">PSD2</option><option value="PSD3">PSD3</option>
                    <option value="PSD4">PSD4</option><option value="PSD5">PSD5</option><option value="PSD6">PSD6</option>
                    <option value="PSD7">PSD7</option>
                  </select>
                </div>

                <div className="pd-field">
                  <label>{t('dsiDate')} ({t('date.formatYMD')})</label>
                  <DateBox
                    name="dsi_date"
                    valueISO={dsiDateISO}
                    onChangeISO={setDsiDateISO}
                    placeholder={t('date.formatYMD')}
                  />
                </div>

                <div className="pd-field" style={{ gridColumn: '1 / -1' }}>
                  <label>{t('patient.order.uploadTom')}</label>
                  <input
                    type="file"
                    accept=".tom"
                    onChange={(e) => setMfgFile(e.target.files?.[0] || null)}
                    required={replacement || specialCase}
                  />
                </div>

                <div className="pd-field">
                  <label>{t('patient.order.manufacturingCode')}</label>
                  <select
                    value={mfgCode}
                    onChange={(e) => setMfgCode(e.target.value)}
                    disabled={!mfgFile}
                    required={!!mfgFile}
                  >
                    <option value="">{t('deptMsg.select')}</option>
                    {MFG_CODES.map(code => (<option key={code} value={code}>{code}</option>))}
                  </select>
                </div>

                {showStatusBlock && (
                  <div className="pd-status" style={{ gridColumn: '1 / -1' }}>
                    {uploadStatus.state === 'uploading' || uploadStatus.state === 'waiting' ? (
                      <div className="pd-progress">
                        <div className="pd-progress-track">
                          <div className="pd-progress-bar" style={{ width: `${uploadStatus.pct || 0}%` }} />
                        </div>
                        <div className="pd-progress-text">
                          {uploadStatus.message} {uploadStatus.pct}%
                        </div>
                      </div>
                    ) : (
                      <div
                        className={`pd-toast-inline ${
                          uploadStatus.state === 'success' ? 'pd-toast-inline--success' : 'pd-toast-inline--error'
                        }`}
                        role="status"
                        aria-live="polite"
                      >
                        {uploadStatus.message}
                      </div>
                    )}
                  </div>
                )}

                {mfgError && (
                  <div className="pd-error" style={{ gridColumn: '1 / -1' }}>
                    {mfgError}
                  </div>
                )}

                <button
                  type="submit"
                  className="pd-btn"
                  disabled={isSubmitting || (!orderValue && !replacement) || (specialCase && !specialCaseData)}
                  aria-busy={isSubmitting ? 'true' : 'false'}
                >
                  {isSubmitting ? t('submitting') : t('upload')}
                </button>
              </fieldset>
            </form>

            <div className="pd-subsection" style={{ marginTop: 18 }}>
              <h4 className="mono">{t('patient.order.prevTomFiles')}</h4>
              {tomLoading ? (
                <div>{t('loading')}</div>
              ) : tomFiles.length === 0 ? (
                <div className="pd-muted">{t('patient.order.noTomFiles')}</div>
              ) : (
                <ul className="pd-filelist">
                  {tomFiles.map((f, idx) => (
                    <li key={(f.url || '') + idx} className="pd-fileitem">
                      {f.url ? (
                        <a href={f.url} target="_blank" rel="noreferrer" className="mono">
                          {f.name}
                        </a>
                      ) : (
                        <span className="mono">{f.name}</span>
                      )}
                      {f.created_at && (
                        <span className="pd-filetime">{formatPortalDateTime(f.created_at)}</span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
      </div>

      {showSpecialCaseModal && (
        <SpecialCaseModal
          onClose={() => { setShowSpecialCaseModal(false); if (!specialCaseData) setSpecialCase(false); }}
          onSave={(data) => { setSpecialCaseData(data); setShowSpecialCaseModal(false); }}
          initial={{
            ...(specialCaseData || {}),
            patientName: patient ? `${patient.first_name} ${patient.last_name}` : '',
            dob: patient?.date_of_birth || '',
            age: patient?.age || '',
            ageDisplay: formatPatientAge(patient?.age || ''),
            dsi_date: isoToDisplay(dsiDateISO || (patient?.dsi_date || '').replace(/-/g, '/')),
            center:
              (service_center_detail && service_center_detail.name) ||
              (patient?.service_center?.name) ||
              '',
          }}
        />
      )}
    </div>
  );
};

export default PatientDetail;
